import { useEffect, useState } from "react";
import { useCart } from "../../../../hooks/useCart";
import { useNavigate, useParams } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
import usePendingCheckoutSession from "../../../../hooks/usePendingCheckoutSession";
import { useGetOrderDetails } from "../../../../hooks/api/queries/useOrder";
import { useCheckoutHelper } from "../../../../hooks/useCheckoutHelper";
import { useSelector } from "react-redux";
import { useGetCheckoutPaymentProviders } from "../../../../hooks/useGetCheckoutPaymentProviders";
import {
  useGetAllChartOfAccounts,
  useGetStoreDetails,
} from "../../../../hooks/api/queries";

export const useCheckoutView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { raven } = useSelector((state) => state.paymentsReducer);
  const {
    customer,
    payment,
    store_wallet,
    order_note,
    ledger_account_id,
    setPaymentMethod: addPaymentMethod,
    updatePayment,
    setStoreWallet,
    setOrderNote,
    setOrderType,
    setTable,
    setCustomerInfo,
    resetCheckoutView,
  } = useCheckoutHelper();
  const paymentMethod = payment?.payment_method;
  const {
    totalCalculatedPrice,
    submitOrder,
    cartItems,
    isCreatingOrder,
    storeDetails,
    store_id,
  } = useCart({});
  const [checkoutOptions] = useState(() => {
    const options = [
      {
        name: "Cash",
        value: "cash",
        key: "pay_with_cash",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_checkout_with_cash_payment",
        showOnLocalSetup: true,
      },
      {
        name: "Card",
        value: "card",
        key: "pay_with_card",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_checkout_with_card_payment",
        showOnLocalSetup: true,
      },
      {
        name: "Transfer",
        value: "transfer",
        key: "pay_with_transfer",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_checkout_with_transfer",
        showOnLocalSetup: true,
      },
      {
        name: "Store Wallet",
        value: "store_wallet",
        key: "pay_with_store_wallet",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_checkout_with_store_wallet",
      },
      {
        name: "Credit",
        value: "credit",
        key: "pay_with_credit",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_issue_credit_sales_to_customers",
        showOnLocalSetup: true,
      },
      {
        name: "Split",
        value: "split",
        key: "pay_with_split",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_checkout_with_split_payment",
        showOnLocalSetup: true,
      },
    ];

    // PR if warehouse is enabled
    if (storeDetails?.is_warehouse) {
      options.push({
        name: "PR",
        value: "pr",
        key: "pay_with_pr",
        permissionParentKey: "Inventory Management",
        permissionChildKey: "can_checkout_with_warehouse",
        showOnLocalSetup: true,
      });
    }

    return options;
  });

  const [views, setViews] = useState({
    isCardPayment: false,
    isTransferPayment: false,
  });
  const { isPendingCheckout, handlePendCheckout } = usePendingCheckoutSession({
    isEnabled: false,
    onPendSuccess: () => {
      navigate(pathConstants.SELL_MODE({ store_id: store_id }));
    },
  });
  const { data: orderDetails, isLoading: isLoadingOrderDetails } =
    useGetOrderDetails({
      id: params?.order_id,
      isEnabled: true,
    });

  const { data: mainStoreDetails } = useGetStoreDetails(params?.store_id);
  const { hasAtleastOneProvider } = useGetCheckoutPaymentProviders();
  const { data: bankAccounts } = useGetAllChartOfAccounts({
    is_bank_account: true,
  });

  const isAccountingActivated = Boolean(
    storeDetails?.allow_chart_account_selection_on_checkout,
  );
  const hasRegisteredAccounts = Boolean(bankAccounts?.data?.length > 0);
  const isAccountRequired = isAccountingActivated && hasRegisteredAccounts;

  const setPaymentMethod = (value, key) => {
    addPaymentMethod({
      payment_method: value,
      [key]: { amount: orderDetails?.total_amount || totalCalculatedPrice },
    });
  };

  const processOrder = () => {
    if (
      isAccountRequired &&
      ["transfer", "card", "split"].includes(paymentMethod) &&
      !ledger_account_id
    ) {
      return;
    }

    const paymentKey = payment?.pay_with_card
      ? "isCardPayment"
      : payment?.pay_with_transfer
        ? "isTransferPayment"
        : null;

    if (hasAtleastOneProvider && paymentKey)
      return setViews((prev) => ({ ...prev, [paymentKey]: true }));

    submitOrder();
  };

  useEffect(() => {
    addPaymentMethod(null);
  }, [cartItems]);

  useEffect(() => {
    if (!orderDetails) return;

    resetCheckoutView();

    if (orderDetails?.notes) setOrderNote(orderDetails?.notes);

    if (orderDetails?.order_type) setOrderType(orderDetails?.order_type);

    if (orderDetails?.table)
      setTable({
        name: orderDetails?.table?.name,
        order_table_id: orderDetails?.table?.id,
        diners: 1,
      });

    if (orderDetails?.customer)
      setCustomerInfo({
        customer_id: orderDetails?.customer?.id,
        customerId: orderDetails?.customer?.id,
        customerPhoneNumber: `${orderDetails?.customer.phone}`,
        customerName: `${orderDetails?.customer?.first_name || ""} ${orderDetails?.customer?.last_name || ""}`,
        customerEmail: orderDetails?.customer?.email || "",
        customerPhoneCode: orderDetails?.customer?.phone_code || "",
      });

    return () => {
      if (orderDetails) resetCheckoutView();
    };
  }, [orderDetails]);

  useEffect(() => {
    if (
      raven?.hasCompletedPayment &&
      storeDetails?.support_raven_payment_on_checkout_flow
    )
      submitOrder({
        paymentProps: {
          payment_provider: raven?.paymentType,
          payment_reference: raven?.rrn,
        },
      });
  }, [raven?.hasCompletedPayment]);

  return {
    checkoutOptions,
    paymentMethod,
    orderNote: order_note,
    isCreatingOrder,
    customer,
    payment,
    store_wallet,
    totalCalculatedPrice,
    storeDetails,
    isPendingCheckout,
    orderDetails,
    isLoadingOrderDetails,
    views,
    submitOrder,
    mainStoreDetails,
    setCustomerInfo,
    updatePayment,
    setStoreWallet,
    setViews,
    processOrder,
    handlePendCheckout,
    setPaymentMethod,
    setOrderNote,
    isAccountRequired,
    bankAccounts,
    ledger_account_id,
  };
};
